import * as React from 'react'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '~/client/components/ui/dropdown-menu'
import { Button } from '~/client/components/ui/button'

// Placeholder components for missing icons
const ChevronDownIcon = ({ size = 20, className, ...props }: React.SVGProps<SVGSVGElement> & { size?: number | string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} {...props}>
    <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const ChevronUpIcon = ({ size = 20, className, ...props }: React.SVGProps<SVGSVGElement> & { size?: number | string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} {...props}>
    <path d="M18 15L12 9L6 15" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const PlusIcon = ({ size = 16, className, ...props }: React.SVGProps<SVGSVGElement> & { size?: number | string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} {...props}>
    <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

export type EventItem = {
  id: string
  title: string
  venueGroupName?: string
  dateLabel?: string
  ownerName?: string
  capacityLabel?: string
  isSpaceInEvent?: boolean
  slug: string
}

export type EventsDropdownProps = {
  /**
   * Preferred prop name for providing items
   */
  options?: EventItem[]
  /**
   * Backward compatible prop (alias of options)
   */
  events?: EventItem[]
  /**
   * Whether events are loading; renders skeleton rows
   */
  isLoading?: boolean
  onSelect?: (event: EventItem) => void
  onCreateNew?: () => void
  triggerClassName?: string
}

const IMG = {
  chevronDown: ChevronDownIcon,
  chevronUp: ChevronUpIcon,
  plus: PlusIcon,
} as const

// Removed DEFAULT_EVENTS per requirement

export default function EventsDropdown({ options, events, isLoading, onSelect, onCreateNew, triggerClassName }: EventsDropdownProps) {
  const items: EventItem[] = React.useMemo(() => {
    if (options && options.length > 0) return options
    if (events && events.length > 0) return events
    return []
  }, [options, events])
  const [open, setOpen] = React.useState(false)

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="revelOutline" size="revel36" className={`rounded-[8px] pl-4 pr-3 ${triggerClassName ?? ''}`}>
          <span className="text-[#8a3dff]">My Events</span>
          {open ? <IMG.chevronUp className="size-5" /> : <IMG.chevronDown className="size-5" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={10}
        collisionPadding={16}
        className="w-[470px] max-w-[calc(100vw-32px)] rounded-[12px] !p-0 bg-white border border-neutral-200 shadow-[0px_4px_4px_10px_rgba(0,0,0,0.04)] flex flex-col"
      >
        {/* Scrollable events list */}
        <div className="max-h-[400px] overflow-y-auto rounded-t-[12px]">
          {/* List items styled to match Figma spacing and dividers */}
          {isLoading ? (
            Array.from({ length: 4 }).map((_, idx) => (
              <React.Fragment key={`skeleton-${idx}`}>
                <div className="px-4 py-[18px]">
                  <div className="h-4 w-3/4 bg-muted animate-pulse rounded" />
                  <div className="mt-2 h-4 w-1/2 bg-muted/70 animate-pulse rounded" />
                </div>
                {idx < 3 ? <div className="h-px bg-[#e4e1e9]" /> : null}
              </React.Fragment>
            ))
          ) : items.length > 0 ? (
            items.map((ev, idx) => (
              <React.Fragment key={ev.id}>
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault()
                    onSelect?.(ev)
                    setOpen(false)
                  }}
                  className="px-4 py-[18px] cursor-pointer rounded-none hover:bg-[rgba(138,61,255,0.06)] data-[highlighted]:bg-[rgba(138,61,255,0.06)] data-[highlighted]:text-[#55534e]"
                >
                  <div className="flex flex-col w-full">
                    <div className="text-[14px] font-medium text-[#55534e] leading-[14px]">{ev.title}</div>
                    {(ev.venueGroupName || ev.dateLabel) && (
                      <div className="mt-1 flex items-center gap-1.5 text-[14px] font-medium text-[#9b9b9a]">
                        {[ev.venueGroupName, ev.dateLabel].filter(Boolean).map((part, i) => (
                          <React.Fragment key={i}>
                            {i > 0 && <span className="text-[#c8c5cc]">•</span>}
                            <span>{part}</span>
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                  </div>
                </DropdownMenuItem>
                {idx < items.length - 1 ? <DropdownMenuSeparator className="h-px bg-[#e4e1e9] mx-0" /> : null}
              </React.Fragment>
            ))
          ) : (
            <div className="px-4 py-[18px] text-sm text-muted-foreground">No events yet</div>
          )}
        </div>

        {/* Sticky Create New Event button */}
        <div className="sticky bottom-0 bg-white rounded-b-[12px]">
          <div className="h-px bg-[#e4e1e9]" />
          <button
            type="button"
            onClick={() => {
              onCreateNew?.()
              setOpen(false)
            }}
            className="flex w-full items-center gap-2 p-4 bg-[#8a3dff] text-white rounded-b-[12px]"
          >
            <IMG.plus className="size-4" />
            <span className="text-[14px] font-medium leading-6">Create New Event</span>
          </button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
